/**
 * Kids Infinite Scroll Module
 * Provides infinite scroll functionality for the Kids block
 */

import { onDOMReady, devLog } from './utils.js';

class KidsInfiniteScroll {
    constructor() {
        this.kidsBlocks = document.querySelectorAll('.kids-block--archive[data-infinite-scroll="true"]');
        this.loadingStates = new Map();
        this.currentPage = new Map();
        this.searchTerms = new Map();
        
        this.init();
    }

    init() {
        if (this.kidsBlocks.length === 0) return;
        
        this.kidsBlocks.forEach((block, index) => {
            this.setupInfiniteScroll(block, index);
        });
    }

    setupInfiniteScroll(block, index) {
        const grid = block.querySelector('.kids-grid');
        const loadMoreTrigger = block.querySelector('.kids-block__load-more-trigger');
        
        if (!grid || !loadMoreTrigger) return;

        // Initialize state
        this.currentPage.set(index, 1);
        this.searchTerms.set(index, '');
        this.loadingStates.set(index, false);

        // Setup intersection observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.loadingStates.get(index)) {
                    this.loadMoreKids(block, index);
                }
            });
        }, {
            rootMargin: '100px'
        });

        observer.observe(loadMoreTrigger);

        // Listen for search updates
        grid.addEventListener('kidsSearchUpdate', (e) => {
            const newSearchTerm = e.detail.searchTerm;
            const oldSearchTerm = this.searchTerms.get(index);
            
            // Only reset if search term actually changed
            if (newSearchTerm !== oldSearchTerm) {
                this.searchTerms.set(index, newSearchTerm);
                
                // Reset pagination when search changes
                this.currentPage.set(index, 1);
                
                // Clear any loaded content beyond initial page
                this.clearLoadedContent(grid);
                
                // If there's a search term, disable infinite scroll temporarily
                // since we're doing server-side searching
                if (newSearchTerm.trim()) {
                    this.loadingStates.set(index, true); // Prevent loading
                    loadMoreTrigger.style.display = 'none';
                } else {
                    // Re-enable infinite scroll when search is cleared
                    this.loadingStates.set(index, false);
                    loadMoreTrigger.style.display = 'block';
                    loadMoreTrigger.style.opacity = '1';
                    loadMoreTrigger.classList.remove('kids-block__loading--active', 'kids-block__error--active');
                    loadMoreTrigger.innerHTML = '';
                }
            }
        });

        // Store observer for cleanup
        block._infiniteScrollObserver = observer;
    }

    clearLoadedContent(grid) {
        // Remove any cards that were loaded via infinite scroll
        // Keep only the initial cards (first page)
        const allCards = grid.querySelectorAll('.prop-card');
        const initialCardsCount = parseInt(grid.dataset.initialCardsCount) || 6;
        
        // Remove cards beyond the initial count
        for (let i = allCards.length - 1; i >= initialCardsCount; i--) {
            allCards[i].remove();
        }
        
        // Show the load more trigger again and reset its state
        const loadMoreTrigger = grid.closest('.kids-block').querySelector('.kids-block__load-more-trigger');
        if (loadMoreTrigger) {
            loadMoreTrigger.style.display = 'block';
            loadMoreTrigger.style.opacity = '1';
            loadMoreTrigger.classList.remove('kids-block__loading--active', 'kids-block__error--active');
            loadMoreTrigger.innerHTML = ''; // Clear any loading/error content
        }
    }

    async loadMoreKids(block, index) {
        if (this.loadingStates.get(index)) {
            devLog('Infinite scroll: Already loading, skipping');
            return;
        }

        const grid = block.querySelector('.kids-grid');
        const loadMoreTrigger = block.querySelector('.kids-block__load-more-trigger');
        const searchTerm = this.searchTerms.get(index);
        const currentPage = this.currentPage.get(index);

        // Don't load more if there's an active search (client-side filtering)
        if (searchTerm && searchTerm.trim()) {
            devLog('Infinite scroll: Search active, skipping load more');
            return;
        }

        // Set loading state
        this.loadingStates.set(index, true);
        this.showLoadingState(loadMoreTrigger);

        try {
            // Increment page number BEFORE making the request
            const nextPage = currentPage + 1;
            const response = await this.fetchMoreKids(block, nextPage, searchTerm);
            
            if (response.success && response.data.html) {
                // Append new kids to grid
                this.appendKidsToGrid(grid, response.data.html);
                
                // Update page count to the next page
                this.currentPage.set(index, nextPage);
                
                // Check if we have more pages
                if (!response.data.hasMore) {
                    this.hideLoadMoreTrigger(loadMoreTrigger);
                }
            } else {
                devLog('Failed to load more kids:', response);
            }
        } catch (error) {
            devLog('Error loading more kids:', error);
            this.showErrorState(loadMoreTrigger);
        } finally {
            this.loadingStates.set(index, false);
            this.hideLoadingState(loadMoreTrigger);
        }
    }

    async fetchMoreKids(block, page, searchTerm) {
        const blockId = block.dataset.blockId;
        const maxItems = parseInt(block.dataset.maxItems) || 6;
        const taxonomyFilterJson = block.dataset.taxonomyFilter || '[]';
        const taxonomyFilter = JSON.parse(taxonomyFilterJson);
        const randomize = block.dataset.randomize === 'true';
        // Partner badges are always shown (no longer a setting)
        const showPartnerBadges = true;
        const showPhoto = block.dataset.showPhoto === 'true';
        const showExcerpt = block.dataset.showExcerpt === 'true';
        const nonce = block.dataset.nonce || '';

        const formData = new FormData();
        formData.append('action', 'load_more_kids');
        formData.append('page', page);
        formData.append('search_term', searchTerm);
        formData.append('max_items', maxItems);
        formData.append('taxonomy_filter', JSON.stringify(taxonomyFilter));
        formData.append('randomize', randomize);
        formData.append('show_partner_badges', showPartnerBadges);
        formData.append('show_photo', showPhoto);
        formData.append('show_excerpt', showExcerpt);
        formData.append('block_id', blockId);
        formData.append('nonce', nonce);

        const response = await fetch(ajaxurl, {
            method: 'POST',
            body: formData
        });

        return await response.json();
    }

    appendKidsToGrid(grid, html) {
        // Create temporary container to parse HTML
        const temp = document.createElement('div');
        temp.innerHTML = html;
        
        const newCards = temp.querySelectorAll('.prop-card');
        const existingCardIds = new Set();
        
        // Get existing card IDs to avoid duplicates
        grid.querySelectorAll('.prop-card').forEach(card => {
            const cardId = card.getAttribute('data-post-id') || card.href;
            if (cardId) {
                existingCardIds.add(cardId);
            }
        });
        
        let addedCount = 0;
        
        newCards.forEach((card, index) => {
            const cardId = card.getAttribute('data-post-id') || card.href;
            
            // Skip if this card already exists
            if (cardId && existingCardIds.has(cardId)) {
                return;
            }
            
            // Add fade-in animation with scale and staggered delay
            card.style.opacity = '0';
            card.style.transform = 'scale(0.95)';
            grid.appendChild(card);
            
            // Track this card ID
            if (cardId) {
                existingCardIds.add(cardId);
            }
            
            // Stagger the animations for a smoother effect
            setTimeout(() => {
                requestAnimationFrame(() => {
                    card.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
                    card.style.opacity = '1';
                    card.style.transform = 'scale(1)';
                });
            }, addedCount * 50); // 50ms delay between each card
            
            addedCount++;
        });
        
        // Only hide load more trigger if we actually received cards but they were all duplicates
        // This indicates we've reached the end of available content
        if (newCards.length > 0 && addedCount === 0) {
            const loadMoreTrigger = grid.closest('.kids-block').querySelector('.kids-block__load-more-trigger');
            if (loadMoreTrigger) {
                this.hideLoadMoreTrigger(loadMoreTrigger);
            }
        }
    }

    showLoadingState(trigger) {
        trigger.innerHTML = '<div class="kids-block__loading">Loading More</div>';
        // Smooth fade in
        requestAnimationFrame(() => {
            trigger.classList.add('kids-block__loading--active');
        });
    }

    hideLoadingState(trigger) {
        // Smooth fade out
        trigger.style.opacity = '0';
        setTimeout(() => {
            trigger.classList.remove('kids-block__loading--active');
            trigger.style.opacity = '';
        }, 300);
    }

    showErrorState(trigger) {
        trigger.innerHTML = '<div class="kids-block__error">Failed to load more kids. <button onclick="location.reload()">Try again</button></div>';
        // Smooth fade in
        requestAnimationFrame(() => {
            trigger.classList.add('kids-block__error--active');
        });
    }

    hideLoadMoreTrigger(trigger) {
        trigger.style.display = 'none';
    }
}

// Initialize when DOM is ready
onDOMReady(() => {
    new KidsInfiniteScroll();
});

export default KidsInfiniteScroll; 